/**
 * API-Client. Alle Endpunkte unter `/api/v1`.
 *
 * Geldbeträge kommen als String und bleiben String. Sobald sie durch `Number`
 * laufen, entstehen genau die Float-Artefakte, die das Projekt loswerden soll.
 */
import type { CalcErrorDto } from "./calcErrors";

const BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Wird gerufen, wenn die API sagt, dass die Anmeldung fehlt oder etwas fällig
 * ist (abgelaufene Sitzung, Passwortwechsel). Die App lädt dann `/auth/me` neu
 * und zeigt die Anmeldung — statt einer roten Fehlermeldung mitten in einer
 * Maske, die niemand einordnen kann.
 */
let onAuthLost: (() => void) | null = null;
export function setAuthLostHandler(handler: (() => void) | null): void {
  onAuthLost = handler;
}

const AUTH_LOST = new Set([
  "unauthorized",
  "password_change_required",
  "totp_setup_required",
  "totp_verify_required",
]);

function checkAuthLost(path: string, status: number, code: string | undefined): void {
  // Die Anmelde-Endpunkte melden ihre Fehler selbst.
  if (path.startsWith("/auth/")) return;
  if ((status === 401 || status === 403) && code && AUTH_LOST.has(code)) onAuthLost?.();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as
    | (T & { error?: string; message?: string })
    | null;

  if (!res.ok) {
    checkAuthLost(path, res.status, body?.error);
    throw new ApiError(
      res.status,
      body?.error ?? "unknown",
      body?.message ?? `${init?.method ?? "GET"} ${path} fehlgeschlagen (${res.status})`,
      body,
    );
  }
  return body as T;
}

export type { CalcErrorDto };

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", ...(body ? { body: JSON.stringify(body) } : {}) }),
  /**
   * Lädt eine Datei und bietet sie zum Speichern an. Ein einfacher Link täte
   * das auch, zeigte aber bei einem Fehler das rohe JSON der API statt einer
   * Meldung in der Maske.
   */
  download: async (path: string, fallbackName: string): Promise<string> => {
    const res = await fetch(`${BASE}${path}`);
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
      checkAuthLost(path, res.status, body?.error);
      throw new ApiError(res.status, body?.error ?? "unknown", body?.message ?? `Download fehlgeschlagen (${res.status})`, body);
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? fallbackName;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return name;
  },
  upload: async <T>(path: string, form: FormData): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, { method: "POST", body: form });
    const body = (await res.json().catch(() => null)) as
      | (T & { error?: string; message?: string })
      | null;
    if (!res.ok) {
      checkAuthLost(path, res.status, body?.error);
      throw new ApiError(res.status, body?.error ?? "unknown", body?.message ?? "Upload fehlgeschlagen", body);
    }
    return body as T;
  },
};

// --- Typen der Antworten ----------------------------------------------------

export type SessionStatus = "PROPOSED" | "CONFIRMED" | "EXCLUDED";

export interface ReceiptDto {
  art: "ABSCHLUSS" | "REPORT";
  kennung: string;
  /** Nur beim Abschluss: Beginn der Kassensitzung. */
  von: string | null;
  zeitpunkt: string | null;
  betrag: string | null;
  betragKorrigiert: string | null;
  anteil: string | null;
  manuell: boolean;
  freigabe: string | null;
}

/** Eine der Sessions, die sich einen aufgeteilten Beleg teilen. */
export interface WorkflowStepDto {
  id: "import" | "review" | "rechnen" | "freigeben" | "auszahlen";
  titel: string;
  text: string;
  anzahl: number | null;
  status: "fertig" | "aktiv" | "wartet" | "leer";
  ziel: string;
}

/** Ein Abrechnungszeitraum: halber Monat, die Einheit einer Auszahlung. */
export interface PeriodDto {
  key: string;
  /** `zweite Julihälfte 2026` */
  label: string;
  /** `Juli 2026` */
  monthLabel: string;
  month: string;
  from: string;
  to: string;
  tage: number;
  /** Alles, was nicht gesperrt ist — einschliesslich freigegebener Tage. */
  offeneTage: number;
  entwurf: number;
  berechnet: number;
  freigegeben: number;
  gesperrteTage: number;
  /** Gibt es für diesen Zeitraum schon eine Auszahlungsperiode? */
  hatAuszahlung: boolean;
  auszahlungStatus: "OPEN" | "LOCKED" | "EXPORTED" | "PAID" | null;
  /** Die Auszahlungsperiode, zum Verlinken. */
  auszahlungId: string | null;
  /** Wann die Überweisungsdatei erzeugt wurde. */
  exportiertAm: string | null;
  trinkgeld: string | null;
  /** Aus der Historienmigration übernommen, nicht selbst gerechnet. */
  istAltbestand: boolean;
}

/**
 * Wo die Arbeit am Zeitraum steht: in Arbeit; neue Abrechnung fällig (die
 * Hälfte davor ist abgeschlossen, diese vorbei und ohne Daten); läuft noch;
 * oder abgeschlossen, ohne dass die nächste Hälfte begonnen hat.
 */
export type WorkflowStand = "in_arbeit" | "neu" | "laeuft_noch" | "abgeschlossen";

export interface WorkflowDto {
  periode: PeriodDto | null;
  stand: WorkflowStand | null;
  /** Die zuletzt abgeschlossene Hälfte, solange die neue noch keine Daten hat. */
  vorige: PeriodDto | null;
  perioden: PeriodDto[];
  schritte: WorkflowStepDto[];
  quellen: { typ: string; vorhanden: boolean; dateiname: string | null }[];
}

export interface ParticipantDto {
  sessionId: string;
  businessDate: string;
  workstationName: string;
  zBon: string | null;
  cardTotal: string | null;
  tipAmount: string | null;
  anteil: string | null;
  /** Anteil in Prozent, eine Nachkommastelle. Von der API gerechnet. */
  anteilProzent: string | null;
  istDiese: boolean;
}

/** Ein Beleg, der auf mehrere Betriebstage aufgeteilt wurde. */
export interface SharedReceiptDto {
  art: "ABSCHLUSS" | "REPORT";
  kennung: string;
  zeitpunkt: string | null;
  betrag: string | null;
  beteiligte: ParticipantDto[];
  zBonSumme: string | null;
  /** Trinkgeld aller Beteiligten zusammen. Dieser Betrag steht fest. */
  trinkgeldSumme: string | null;
}

/** Was eine markierte Session erst verständlich macht (siehe sessionContext.ts). */
export interface SessionContextDto {
  geteilterBeleg: SharedReceiptDto | null;
  geschwister: {
    sessionId: string;
    zBon: string | null;
    cardTotal: string | null;
    tipAmount: string | null;
  }[];
}

/** Umsatz früherer Abende, der in einem späteren Gerätewert noch einmal steckt. */
export interface UebertragDto {
  /** Welcher Wert zu hoch ist: Terminal (`cardTotal`) oder Kasse (`zBon`). */
  feld: "cardTotal" | "zBon";
  /** Summe aller übertragenen Abende. */
  betrag: string;
  /** Der Wert ohne die Überträge. */
  vorschlag: string | null;
  /** `false`, wenn der Abzug das Trinkgeld negativ machte: dann steckt der Abend hier nicht drin. */
  passt: boolean;
  /** Die Abende, nach Datum; mehrere, wenn der Schnitt mehrmals vergessen wurde. */
  von: { sessionId: string; datum: string; betrag: string }[];
}

/** Ein Trinkgeldtopf, wie ihn die Berechnung einer Kasse zuordnet. */
export interface TopfDto {
  id: string;
  name: string;
}

export interface CashSessionDto {
  id: string;
  workstationId: string;
  workstationName: string;
  actsAsWorkstationId: string | null;
  actsAsWorkstationName: string | null;
  /** Topf nur für diesen Tag, abweichend von den Stammdaten. */
  poolIdOverride: string | null;
  /** Wohin das Trinkgeld fließt, aufgelöst wie in der Berechnung; `null` heißt ohne Topf. */
  topf: TopfDto | null;
  /** Der Topf laut Stammdaten. */
  topfVorgabe: TopfDto | null;
  proposedBusinessDate: string;
  businessDate: string;
  dateChanged: boolean;
  zBon: string | null;
  cardTotal: string | null;
  tipAmount: string | null;
  tipAmountOverride: string | null;
  flags: string[];
  hasBlockingFlag: boolean;
  status: SessionStatus;
  splitOfSessionId: string | null;
  reason: string | null;
  links: { posClosingId: string | null; terminalReportId: string | null; amountShare: string | null }[];
  /** Die Rohzeilen hinter der Session; nur in den Detailansichten gefüllt. */
  receipts?: ReceiptDto[];
  /** Geschwister-Sessions und geteilte Belege; nur gefüllt, wenn es sie gibt. */
  kontext?: SessionContextDto | null;
  /**
   * Folge-Session eines vergessenen Kassenschnitts: je betroffenem Wert die
   * von Hand erfassten Abende und der fertig gerechnete Vorschlag. Leer, wenn
   * nichts übertragen wurde.
   */
  uebertraege?: UebertragDto[];
  /** Ursprung: wohin der erfasste Betrag vermutlich weitergelaufen ist. */
  uebertragNach?: { betrag: string; folge: { sessionId: string; datum: string } | null } | null;
}

export interface WorkstationDayDto {
  workstationId: string;
  workstationName: string;
  zBon: string | null;
  cardTotal: string | null;
  tipAmount: string | null;
  hasZBon: boolean;
  hasCardTotal: boolean;
  flags: string[];
  sessionIds: string[];
  /** Wohin das Trinkgeld dieser Zeile fließt; `null` heißt ohne Topf. */
  topf: TopfDto | null;
  /** Der Topf laut Stammdaten. Weicht `topf` ab, gilt das nur für diesen Tag. */
  topfVorgabe: TopfDto | null;
}

/** Die drei Arten zu rechnen, wie die Buchhaltung sie nennt. */
export type Berechnungsart = "alle" | "einzeln" | "teil";

/** Wie ein Tag gerechnet wird, eingeordnet nach seiner Aufteilung (`services/berechnung.ts`). */
export interface BerechnungDto {
  art: Berechnungsart;
  /** Topfnamen je Gruppe, nach der Reihenfolge der Stammdaten. */
  gruppen: string[][];
  /** Dieselbe Aufteilung wie die Vorgabe für neue Tage. */
  istVorgabe: boolean;
  /** `false`, solange der Tag nie gerechnet wurde; dann gilt die Vorgabe. */
  festgelegt: boolean;
  vorgabe: { art: Berechnungsart; gruppen: string[][] };
}

export interface PoolGroupTemplateDto {
  id: string;
  name: string;
  /** Listen von Pool-Codes, `["*"]` heißt alle zusammen. */
  definition: string[][];
  isDefault: boolean;
  /** Wie die Vorlage heute rechnen würde. */
  art: Berechnungsart;
  gruppen: string[][];
}

/** Eine Zeile der Auswertung: ein Monat (`2026-07`) oder das Jahr (`2026`). */
export interface DurchschnittDto {
  zeitraum: string;
  trinkgeld: string;
  abende: number;
  personen: number;
  personenAbende: number;
  stunden: string | null;
  proStunde: string | null;
  proAbendUndPerson: string | null;
  proPersonImMonat: string | null;
  stundenVollstaendig: boolean;
}

export interface AuswertungDto {
  jahr: string;
  jahre: string[];
  /** Der letzte ausgezahlte Tag, der eingeht. */
  stand: string | null;
  monate: DurchschnittDto[];
  gesamt: DurchschnittDto;
}

export interface BusinessDaySummary {
  businessDate: string;
  /** `LEGACY_IMPORT` für übernommene Altbestandstage, sonst `CALCULATED`. */
  source?: string | null;
  eventTitle: string | null;
  status: "DRAFT" | "CALCULATED" | "APPROVED" | "LOCKED";
  tipTotal: string | null;
  isBalanced: boolean | null;
  /** Wie der Tag gerechnet wird; vor dem ersten Lauf die Vorgabe. */
  berechnung?: BerechnungDto;
  /** Warum der Tag nicht freigabefähig ist, obwohl keine Kasse offen ist. */
  calcErrors?: CalcErrorDto[];
  /** Hinweise, die nicht blockieren. */
  calcWarnings?: CalcErrorDto[];
  openBlockingFlags: string[];
  canApprove: boolean;
  calculatedAt: string | null;
  approvedAt: string | null;
}

export interface BusinessDayDetail {
  businessDate: string;
  /** Der vorige und der nächste Betriebstag, den es gibt — zum Blättern. */
  nachbarn: { vorher: string | null; nachher: string | null };
  /** Nur bei gesperrten Tagen: Zeilen, die nach der Sperre eingegangen sind und nicht in der Auszahlung stecken. */
  nachSperre: { stunden: number; kassen: number; seit: string | null } | null;
  eventTitle: string | null;
  status: BusinessDaySummary["status"];
  /** `null`, solange der Tag nie gerechnet wurde. */
  calculatedAt: string | null;
  approvedAt: string | null;
  berechnung: BerechnungDto;
  groups: { id: string; label: string | null; pools: { id: string; code: string; name: string }[] }[];
  pools: {
    poolId: string;
    poolCode: string;
    poolName: string;
    grossRevenue: string | null;
    expectedRevenue: string | null;
    tipAmount: string | null;
    totalHours: string | null;
    unallocatedAmount: string | null;
  }[];
  distributions: {
    id: string;
    personalNr: number;
    name: string;
    timeClockId: number | null;
    /** Beim Altbestand leer: das Archiv führt keinen Arbeitsbereich (Spec 3.4). */
    workArea: string | null;
    source: "CALCULATED" | "LEGACY_IMPORT";
    hours: string | null;
    shareBeforeRounding: string | null;
    amount: string | null;
    roundingAdjustment: boolean;
    roundingDelta: string | null;
    isLocked: boolean;
  }[];
  distributionTotal: string | null;
  hasLegacyRows: boolean;
  control: {
    sumPools: string | null;
    sumDistributed: string | null;
    /** Trinkgeld, das im Topf blieb, weil dort niemand gearbeitet hat. */
    sumUnallocated?: string | null;
    warnings?: CalcErrorDto[];
    isBalanced: boolean;
    errors: { code: string; message: string }[];
    calculatedAt: string | null;
  } | null;
}

export interface WorkstationDto {
  /** Kommt in keinem automatischen Import vor; Werte werden von Hand erfasst. */
  manualOnly?: boolean;
  id: string;
  name: string;
  posTerminalId: string | null;
  poolId: string | null;
  poolCode: string | null;
  excludedFromPool: boolean;
  /** Terminals werden getauscht; die Zuordnung gilt nur in diesem Zeitraum (Spec 10.2). */
  validFrom: string | null;
  validTo: string | null;
  active: boolean;
}

export interface PoolDto {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  validFrom: string | null;
  validTo: string | null;
  active: boolean;
}

export interface HoursEntryDto {
  id: string;
  personalNr: number;
  name: string | null;
  workArea: string;
  tipEligible: boolean;
  entryType: string;
  hoursGross: string | null;
  hoursOverride: string | null;
  effectiveHours: string | null;
  overrideReason: string | null;
  startTime: string | null;
  endTime: string | null;
  isManual: boolean;
}

export interface ImportBatchDto {
  /** Warum Zeilen nicht übernommen wurden. Von der API gezählt. */
  skipSummary?: { grund: string; anzahl: number }[] | null;
  id: string;
  sourceType: string;
  filename: string | null;
  status: string;
  rowCount: number;
  importedCount: number;
  skippedCount: number;
  periodFrom: string | null;
  periodTo: string | null;
  createdAt: string | null;
  committedAt: string | null;
  warningCount: number;
}
